import { useQuery, useQueryClient } from '@tanstack/react-query';
import { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  Breadcrumb,
  type BreadcrumbItem,
} from '@doger/ui/components/ui/breadcrumb';
import { Card } from '@doger/ui/components/ui/card';
import { Skeleton } from '@doger/ui/components/ui/skeleton';
import type { AIModel } from '@doger/core/modules/chat';
import { ChatMessages, ModelToView } from '@doger/core/modules/chat';

import { $$privateApi } from '@doger/admin/shared/api';

import styles from './chat-details.module.scss';

export const ChatDetailsPage = memo(() => {
  const queryClient = useQueryClient();
  const { userId, sessionId } = useParams<{
    userId: string;
    sessionId: string;
  }>();

  const { data: user } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => $$privateApi.findOneUser({ id: userId! }),
    enabled: !!userId,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-chats', userId, sessionId],
    queryFn: async () => {
      const data = await $$privateApi.findUserChat({
        userId: userId!,
        sessionId: sessionId!,
      });

      const messages = data.events
        .filter(
          (event) =>
            !!event.content &&
            (event.content.role === 'user' || event.content.role === 'model')
        )
        .flatMap((event) =>
          event.content.parts.map((part, index) => ({
            ...part,
            role: event.content.role,
            id: `${event.id}_${index}`,
          }))
        );

      // We always will have a message in session
      // If the last message is a user message, we need to invalidate the query to get the response from the model
      if (messages.at(-1)?.role === 'user') {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['user-chats', userId, sessionId],
          });
        }, 30_000);
      }

      return data;
    },
    enabled: !!sessionId,
    staleTime: 0,
  });

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: 'Users', href: '/users' },
      {
        label:
          user?.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : (user?.firstName ?? user?.lastName ?? 'User'),
        href: `/users/${userId}`,
      },
      { label: data?.meta.title ?? sessionId ?? 'Chat' },
    ],
    [userId, sessionId, user, data?.meta.title]
  );

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <Breadcrumb items={breadcrumbItems} />
          <div className={styles.loading}>
            <Skeleton />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <Breadcrumb items={breadcrumbItems} />
          <div className={styles.error}>
            {error ? `Error loading user: ${error.message}` : 'Chat not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <Breadcrumb items={breadcrumbItems} />

        <div className={styles.chatInfo}>
          <Card className={styles.chatHeaderCard}>
            <div className={styles.chatHeader}>
              <div className={styles.chatTitle}>
                <h1>{data.meta.title}</h1>

                <p className={styles.chatMeta}>
                  <span>SessionId: {data.meta.sessionId}</span>
                  <span>Status: {data.meta.status}</span>
                  <span>
                    Last Updated:{' '}
                    {new Date(data.lastUpdateTime * 1000).toLocaleString()}
                  </span>
                  <span>
                    Model: {ModelToView.toView(data.appName as AIModel).name}
                  </span>
                </p>
              </div>
            </div>
          </Card>

          <div className={styles.messages}>
            <ChatMessages session={data} />
          </div>
        </div>
      </div>
    </div>
  );
});
